import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, createTextVNode as _createTextVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, renderSlot as _renderSlot } from "vue";
const _hoisted_1 = {
    key: 0,
    class: "summary-header"
};
const _hoisted_2 = { class: "summary-title" };
const _hoisted_3 = {
    key: 1,
    class: "compact-header"
};
const _hoisted_4 = { class: "compact-title" };
const _hoisted_5 = ["src"];
const _hoisted_6 = { class: "chart-area" };
const _hoisted_7 = { class: "axis-chart-row" };
const _hoisted_8 = { class: "y-axis" };
const _hoisted_9 = { class: "chart-bars" };
const _hoisted_10 = {
    key: 0,
    class: "empty-state"
};
const _hoisted_11 = ["src"];
const _hoisted_12 = {
    key: 0,
    class: "chart-labels"
};
const _hoisted_13 = {
    key: 1,
    class: "compact-totals"
};
const _hoisted_14 = {
    key: 2,
    class: "breakdown"
};
const _hoisted_15 = { class: "breakdown-left" };
const _hoisted_16 = { class: "breakdown-right" };
const _hoisted_17 = { class: "breakdown-amount amount-income" };
const _hoisted_18 = ["src"];
const _hoisted_19 = { class: "breakdown-right" };
const _hoisted_20 = { class: "breakdown-amount" };
const _hoisted_21 = {
    key: 0,
    class: "breakdown-sublist"
};
const _hoisted_22 = ["onClick"];
const _hoisted_23 = { class: "breakdown-left" };
const _hoisted_24 = { class: "breakdown-name" };
const _hoisted_25 = { class: "breakdown-right" };
const _hoisted_26 = { class: "breakdown-amount" };
const _hoisted_27 = ["src"];
const _hoisted_28 = { class: "summary-header" };
const _hoisted_29 = { class: "summary-title" };
const _hoisted_30 = { class: "weekly-chart-row" };
const _hoisted_31 = { class: "weekly-yaxis" };
const _hoisted_32 = { class: "weekly-bars" };
const _hoisted_33 = {
    key: 0,
    class: "empty-state"
};
const _hoisted_34 = ["src"];
const _hoisted_35 = { class: "weekly-labels" };
const _hoisted_36 = {
    key: 1,
    class: "chart-area"
};
const _hoisted_37 = { class: "axis-chart-row" };
const _hoisted_38 = { class: "y-axis" };
const _hoisted_39 = ["onClick"];
const _hoisted_40 = {
    key: 0,
    class: "empty-state empty-state-monthly"
};
const _hoisted_41 = ["src"];
import { computed, ref, onMounted, watch } from 'vue';
import { useAppState } from '@/composables/useAppState';
const chartHeight = 113;
const weeklyChartHeight = 113;
export default /*@__PURE__*/ _defineComponent({
    __name: 'SpendingSummaryCard',
    props: {
        mode: { default: 'overview' },
        data: {},
        categoryLabel: {},
        rangeLabel: {},
        amount: {},
        monthlyData: {},
        weeklyData: {},
        currentMonthIndex: {},
        barColour: { default: '#66CCAA' },
        compact: { type: Boolean, default: false },
        compactTitle: { default: '' },
        loading: { type: Boolean }
    },
    emits: ["category-click", "budget-click", "month-click"],
    setup(__props, { emit: __emit }) {
        const state = useAppState();
        const props = __props;
        const emit = __emit;
        function onMonthBarClick(bar) {
            if (bar.isFuture)
                return;
            emit('month-click', bar);
        }
        const animated = ref(false);
        function replayAnimation() {
            animated.value = false;
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    animated.value = true;
                });
            });
        }
        onMounted(replayAnimation);
        // Replay the 0→height transition whenever new data arrives so bars animate
        // on category change / period change, not just on mount.
        watch(() => props.data, replayAnimation, { deep: true });
        watch(() => props.monthlyData, replayAnimation, { deep: true });
        watch(() => props.weeklyData, replayAnimation, { deep: true });
        const inBarHeight = computed(() => {
            if (!props.data)
                return 0;
            const top = overviewAxisTop.value;
            if (top === 0)
                return 0;
            return Math.round((incomeAmount.value / top) * chartHeight);
        });
        const outBarHeight = computed(() => {
            if (!props.data)
                return 0;
            const top = overviewAxisTop.value;
            if (top === 0)
                return 0;
            return Math.round((props.data.totalOut / top) * chartHeight);
        });
        const CATEGORY_ORDER = ['Income', 'Essentials', 'Lifestyle', 'Future', 'General'];
        function categoryRank(name) {
            const idx = CATEGORY_ORDER.indexOf(name);
            return idx === -1 ? CATEGORY_ORDER.length : idx;
        }
        const outItems = computed(() => (props.data?.breakdown ?? [])
            .filter(b => b.direction === 'out')
            .sort((a, b) => categoryRank(a.name) - categoryRank(b.name)));
        const sortedBreakdown = computed(() => {
            const items = props.data?.breakdown ?? [];
            return [...items].sort((a, b) => categoryRank(a.name) - categoryRank(b.name));
        });
        const incomeAmount = computed(() => {
            const items = props.data?.breakdown ?? [];
            const income = items.find(b => b.name === 'Income' && b.direction === 'in');
            return income?.amount ?? 0;
        });
        const inTotal = computed(() => incomeAmount.value);
        const outTotal = computed(() => props.data?.totalOut ?? 0);
        const incomeColour = computed(() => {
            const items = props.data?.breakdown ?? [];
            const incomeItem = items.find(b => b.name === 'Income' && b.direction === 'in' && b.colour);
            return incomeItem?.colour || '#66CCAA';
        });
        const outExpanded = ref(false);
        // Empty-state is suppressed while loading AND during the brief animation-replay
        // window after fresh data arrives, so we don't flash "no transactions" over the
        // chart before bars draw.
        const overviewEmpty = computed(() => animated.value && !props.loading && !!props.data && inTotal.value === 0 && outTotal.value === 0);
        const weeklyEmpty = computed(() => {
            if (props.loading || !animated.value)
                return false;
            const items = props.weeklyData ?? [];
            if (items.length === 0)
                return false;
            return items.every(b => (b.value ?? 0) === 0);
        });
        const monthlyEmpty = computed(() => {
            if (props.loading || !animated.value)
                return false;
            const items = props.monthlyData ?? [];
            if (items.length === 0)
                return false;
            return items.every(b => (b.value ?? 0) === 0);
        });
        // Routes through state.formatter so the global "Hide Figures" setting
        // (localStorage.randomiseFigures) replaces real numbers with "£####".
        function formatAmount(amount) {
            if (localStorage.getItem('randomiseFigures') === 'true')
                return '£####';
            return state.formatter.format(amount);
        }
        function monthlyBarHeight(value) {
            const top = monthlyAxisTop.value;
            if (top === 0)
                return 0;
            return Math.round((value / top) * chartHeight);
        }
        // Minimum width needed to lay out every monthly bar at its baseline (28px).
        // Applied as inline min-width so the row overflows horizontally when there
        // are more bars than can fit, letting the container scroll. For 12-bar
        // ranges we shrink the per-bar minimum so all months fit without scroll.
        const monthlyRowMinWidth = computed(() => {
            const count = props.monthlyData?.length ?? 0;
            if (count === 0)
                return 0;
            if (count >= 12)
                return 0;
            const perBar = 28;
            const gap = 8;
            const padding = 20; // 10px each side on .monthly-bars
            return count * perBar + (count - 1) * gap + padding;
        });
        // 12+ months: single-letter labels so they fit without horizontal scroll.
        function formatMonthlyLabel(label) {
            const count = props.monthlyData?.length ?? 0;
            if (count >= 12)
                return (label || '').charAt(0);
            return label;
        }
        // Current-bar index for the monthly chart. If the parent supplies one
        // (dynamic range view), trust it; otherwise keep the existing
        // "last bar is current" behaviour so static fallbacks still work.
        const activeMonthIndex = computed(() => {
            if (props.currentMonthIndex !== undefined)
                return props.currentMonthIndex;
            return (props.monthlyData?.length ?? 0) - 1;
        });
        // Weekly chart (single-month view) ---------------------------------------
        const useWeekly = computed(() => {
            return props.mode === 'category' && !!props.weeklyData && props.weeklyData.length > 0;
        });
        // Current week = last entry that is not marked future
        const currentWeekIndex = computed(() => {
            if (!props.weeklyData)
                return -1;
            let idx = -1;
            props.weeklyData.forEach((b, i) => { if (!b.isFuture)
                idx = i; });
            return idx;
        });
        // Pick a "nice" y-axis top so labels are round (step * 3 = top, 3 gridlines)
        function niceAxisTop(max) {
            if (max <= 0)
                return 3000;
            const niceSteps = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000];
            const target = max / 3;
            const step = niceSteps.find(s => s >= target) ?? niceSteps[niceSteps.length - 1];
            return step * 3;
        }
        function axisLabelsFromTop(top) {
            return [top, Math.round((top / 3) * 2), Math.round(top / 3), 0];
        }
        const weeklyAxisTop = computed(() => {
            if (!props.weeklyData)
                return 0;
            return niceAxisTop(Math.max(...props.weeklyData.map(b => b.value), 0));
        });
        const weeklyAxisLabels = computed(() => axisLabelsFromTop(weeklyAxisTop.value));
        const overviewAxisTop = computed(() => {
            if (!props.data)
                return 0;
            return niceAxisTop(Math.max(incomeAmount.value, props.data.totalOut, 0));
        });
        const overviewAxisLabels = computed(() => axisLabelsFromTop(overviewAxisTop.value));
        const monthlyAxisTop = computed(() => {
            if (!props.monthlyData)
                return 0;
            return niceAxisTop(Math.max(...props.monthlyData.map(b => b.value), 0));
        });
        const monthlyAxisLabels = computed(() => axisLabelsFromTop(monthlyAxisTop.value));
        function formatAxis(v) {
            // Honour the "Hide Figures" setting so axis labels don't leak real numbers.
            if (localStorage.getItem('randomiseFigures') === 'true')
                return '£#';
            if (v >= 1000) {
                const k = v / 1000;
                return '£' + (k % 1 === 0 ? k : k.toFixed(1)) + 'k';
            }
            return '£' + v;
        }
        function weeklyBarHeight(bar) {
            if (bar.isFuture)
                return 0;
            const top = weeklyAxisTop.value;
            if (top === 0)
                return 0;
            return Math.round((bar.value / top) * weeklyChartHeight);
        }
        // Fade-out of the bar gradient: start from the base colour, fade to 0 alpha at bottom
        const barColourFade = computed(() => {
            const c = props.barColour;
            // Accept #RRGGBB hex; convert to rgba with alpha 0
            if (/^#([0-9a-f]{6})$/i.test(c)) {
                const r = parseInt(c.slice(1, 3), 16);
                const g = parseInt(c.slice(3, 5), 16);
                const b = parseInt(c.slice(5, 7), 16);
                return `rgba(${r}, ${g}, ${b}, 0)`;
            }
            return 'transparent';
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("div", {
                class: _normalizeClass(["summary-card", { 'summary-card-compact': _ctx.compact }])
            }, [
                (_ctx.mode === 'overview')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        (!_ctx.compact)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                _createElementVNode("span", _hoisted_2, _toDisplayString(_ctx.data?.rangeLabel), 1)
                            ]))
                            : _createCommentVNode("", true),
                        (_ctx.compact && _ctx.compactTitle)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createElementVNode("span", _hoisted_4, _toDisplayString(_ctx.compactTitle), 1),
                                _createElementVNode("img", {
                                    class: "compact-arrow",
                                    src: require('@/assets/arrow-right.png'),
                                    alt: ""
                                }, null, 8, _hoisted_5)
                            ]))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_6, [
                            _createElementVNode("div", _hoisted_7, [
                                _createElementVNode("div", _hoisted_8, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(overviewAxisLabels.value, (v) => {
                                        return (_openBlock(), _createElementBlock("span", {
                                            key: v,
                                            class: _normalizeClass({ 'yaxis-hidden': v === 0 })
                                        }, _toDisplayString(formatAxis(v)), 3));
                                    }), 128))
                                ]),
                                _createElementVNode("div", _hoisted_9, [
                                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "chart-gridlines" }, [
                                        _createElementVNode("div", { class: "chart-gridline" }),
                                        _createElementVNode("div", { class: "chart-gridline" }),
                                        _createElementVNode("div", { class: "chart-gridline" })
                                    ], -1)),
                                    _createElementVNode("div", {
                                        class: "bar bar-in",
                                        style: _normalizeStyle({ height: (animated.value ? inBarHeight.value : 0) + 'px' }),
                                        onClick: _cache[0] || (_cache[0] = ($event) => (emit('category-click', 'Income')))
                                    }, [
                                        _createElementVNode("div", {
                                            class: "bar-in-fill",
                                            style: _normalizeStyle({ backgroundColor: incomeColour.value })
                                        }, null, 4)
                                    ], 4),
                                    _createElementVNode("div", {
                                        class: "bar bar-out",
                                        style: _normalizeStyle({ height: (animated.value ? outBarHeight.value : 0) + 'px' }),
                                        onClick: _cache[1] || (_cache[1] = ($event) => (outExpanded.value = !outExpanded.value))
                                    }, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(outItems.value, (item) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                key: item.name,
                                                class: "bar-segment",
                                                style: _normalizeStyle({ flex: String(item.amount), backgroundColor: item.colour })
                                            }, null, 4));
                                        }), 128))
                                    ], 4),
                                    (overviewEmpty.value)
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                            _createElementVNode("img", {
                                                class: "empty-icon",
                                                src: require('@/assets/search.png'),
                                                alt: ""
                                            }, null, 8, _hoisted_11),
                                            _cache[4] || (_cache[4] = _createElementVNode("span", { class: "empty-text" }, [
                                                _createTextVNode("No transactions available"),
                                                _createElementVNode("br"),
                                                _createTextVNode("for these dates")
                                            ], -1))
                                        ]))
                                        : _createCommentVNode("", true)
                                ])
                            ]),
                            (!_ctx.compact)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_12, _cache[6] || (_cache[6] = [
                                    _createElementVNode("span", { class: "axis-spacer" }, null, -1),
                                    _createElementVNode("span", null, "In", -1),
                                    _createElementVNode("span", null, "Out", -1)
                                ])))
                                : (_openBlock(), _createElementBlock("div", _hoisted_13, [
                                    _cache[7] || (_cache[7] = _createElementVNode("span", { class: "axis-spacer" }, null, -1)),
                                    _createElementVNode("span", null, "In: " + _toDisplayString(_ctx.data ? formatAmount(inTotal.value) : ''), 1),
                                    _createElementVNode("span", null, "Out: " + _toDisplayString(_ctx.data ? formatAmount(_ctx.data.totalOut) : ''), 1)
                                ]))
                        ]),
                        (!_ctx.compact)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                                (inTotal.value > 0)
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 0,
                                        class: "breakdown-row",
                                        onClick: _cache[2] || (_cache[2] = ($event) => (emit('category-click', 'Income')))
                                    }, [
                                        _createElementVNode("div", _hoisted_15, [
                                            _createElementVNode("span", {
                                                class: "colour-dot",
                                                style: _normalizeStyle({ backgroundColor: incomeColour.value })
                                            }, null, 4),
                                            _cache[8] || (_cache[8] = _createElementVNode("span", { class: "breakdown-name" }, "Income", -1))
                                        ]),
                                        _createElementVNode("div", _hoisted_16, [
                                            _createElementVNode("span", _hoisted_17, _toDisplayString(formatAmount(inTotal.value)), 1),
                                            _createElementVNode("img", {
                                                class: "breakdown-arrow",
                                                src: require('@/assets/arrow-right.png'),
                                                alt: ""
                                            }, null, 8, _hoisted_18)
                                        ])
                                    ]))
                                    : _createCommentVNode("", true),
                                (outTotal.value > 0)
                                    ? (_openBlock(), _createElementBlock("div", {
                                        key: 1,
                                        class: "breakdown-row",
                                        onClick: _cache[3] || (_cache[3] = ($event) => (outExpanded.value = !outExpanded.value))
                                    }, [
                                        _cache[9] || (_cache[9] = _createElementVNode("div", { class: "breakdown-left" }, [
                                            _createElementVNode("span", {
                                                class: "colour-dot",
                                                style: { backgroundColor: '#413655' }
                                            }),
                                            _createElementVNode("span", { class: "breakdown-name" }, "Outgoing")
                                        ], -1)),
                                        _createElementVNode("div", _hoisted_19, [
                                            _createElementVNode("span", _hoisted_20, _toDisplayString(formatAmount(outTotal.value)), 1),
                                            _createElementVNode("span", {
                                                class: _normalizeClass(["breakdown-chevron", { expanded: outExpanded.value }])
                                            }, "▼", 2)
                                        ])
                                    ]))
                                    : _createCommentVNode("", true),
                                _createVNode(_Transition, { name: "expand" }, {
                                    default: _withCtx(() => [
                                        (outExpanded.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(outItems.value, (item) => {
                                                    return (_openBlock(), _createElementBlock("div", {
                                                        key: item.name,
                                                        class: "breakdown-row breakdown-subrow",
                                                        onClick: ($event) => (emit('category-click', item.name))
                                                    }, [
                                                        _createElementVNode("div", _hoisted_23, [
                                                            _createElementVNode("span", {
                                                                class: "colour-dot",
                                                                style: _normalizeStyle({ backgroundColor: item.colour })
                                                            }, null, 4),
                                                            _createElementVNode("span", _hoisted_24, _toDisplayString(item.name), 1)
                                                        ]),
                                                        _createElementVNode("div", _hoisted_25, [
                                                            _createElementVNode("span", _hoisted_26, _toDisplayString(formatAmount(item.amount)), 1),
                                                            _createElementVNode("img", {
                                                                class: "breakdown-arrow",
                                                                src: require('@/assets/arrow-right.png'),
                                                                alt: ""
                                                            }, null, 8, _hoisted_27)
                                                        ])
                                                    ], 8, _hoisted_22));
                                                }), 128))
                                            ]))
                                            : _createCommentVNode("", true)
                                    ]),
                                    _: 1
                                })
                            ]))
                            : _createCommentVNode("", true)
                    ], 64))
                    : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                        _createElementVNode("div", _hoisted_28, [
                            _createElementVNode("span", _hoisted_29, _toDisplayString(_ctx.rangeLabel), 1),
                            _createElementVNode("span", {
                                class: _normalizeClass(["summary-net", { 'summary-net-positive': (_ctx.amount ?? 0) > 0, 'summary-net-fade': _ctx.amount === undefined }])
                            }, _toDisplayString(_ctx.amount !== undefined ? formatAmount(Math.abs(_ctx.amount)) : ''), 3)
                        ]),
                        (useWeekly.value)
                            ? (_openBlock(), _createElementBlock("div", {
                                key: 0,
                                class: "chart-area",
                                style: _normalizeStyle({ '--bar-colour': _ctx.barColour, '--bar-colour-fade': barColourFade.value })
                            }, [
                                _createElementVNode("div", _hoisted_30, [
                                    _createElementVNode("div", _hoisted_31, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(weeklyAxisLabels.value, (v) => {
                                            return (_openBlock(), _createElementBlock("span", {
                                                key: v,
                                                class: _normalizeClass({ 'yaxis-hidden': v === 0 })
                                            }, _toDisplayString(formatAxis(v)), 3));
                                        }), 128))
                                    ]),
                                    _createElementVNode("div", _hoisted_32, [
                                        _cache[11] || (_cache[11] = _createElementVNode("div", { class: "weekly-gridlines" }, [
                                            _createElementVNode("div", { class: "weekly-gridline" }),
                                            _createElementVNode("div", { class: "weekly-gridline" }),
                                            _createElementVNode("div", { class: "weekly-gridline" })
                                        ], -1)),
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.weeklyData, (bar, index) => {
                                            return (_openBlock(), _createElementBlock("div", {
                                                key: bar.label,
                                                class: _normalizeClass(["weekly-bar", {
                                                        'weekly-bar-current': index === currentWeekIndex.value,
                                                        'weekly-bar-future': bar.isFuture,
                                                    }]),
                                                style: _normalizeStyle({ height: (animated.value ? weeklyBarHeight(bar) : 0) + 'px' })
                                            }, null, 6));
                                        }), 128)),
                                        (weeklyEmpty.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_33, [
                                                _createElementVNode("img", {
                                                    class: "empty-icon",
                                                    src: require('@/assets/search.png'),
                                                    alt: ""
                                                }, null, 8, _hoisted_34),
                                                _cache[10] || (_cache[10] = _createElementVNode("span", { class: "empty-text" }, [
                                                    _createTextVNode("No transactions available"),
                                                    _createElementVNode("br"),
                                                    _createTextVNode("for these dates")
                                                ], -1))
                                            ]))
                                            : _createCommentVNode("", true)
                                    ])
                                ]),
                                _createElementVNode("div", _hoisted_35, [
                                    _cache[12] || (_cache[12] = _createElementVNode("span", { class: "weekly-wc" }, "W/C", -1)),
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.weeklyData, (bar) => {
                                        return (_openBlock(), _createElementBlock("span", {
                                            key: bar.label,
                                            class: _normalizeClass({ 'label-future': bar.isFuture })
                                        }, _toDisplayString(bar.label), 3));
                                    }), 128))
                                ])
                            ], 4))
                            : (_openBlock(), _createElementBlock("div", _hoisted_36, [
                                _createElementVNode("div", _hoisted_37, [
                                    _createElementVNode("div", _hoisted_38, [
                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(monthlyAxisLabels.value, (v) => {
                                            return (_openBlock(), _createElementBlock("span", {
                                                key: v,
                                                class: _normalizeClass({ 'yaxis-hidden': v === 0 })
                                            }, _toDisplayString(formatAxis(v)), 3));
                                        }), 128))
                                    ]),
                                    _createElementVNode("div", {
                                        class: _normalizeClass(["chart-area-monthly", { 'no-scroll': (_ctx.monthlyData?.length ?? 0) >= 12 }])
                                    }, [
                                        _createElementVNode("div", {
                                            class: "monthly-bars",
                                            style: _normalizeStyle({
                                                '--bar-colour': _ctx.barColour,
                                                '--bar-colour-fade': barColourFade.value,
                                                minWidth: monthlyRowMinWidth.value + 'px',
                                            })
                                        }, [
                                            _cache[13] || (_cache[13] = _createElementVNode("div", { class: "chart-gridlines" }, [
                                                _createElementVNode("div", { class: "chart-gridline" }),
                                                _createElementVNode("div", { class: "chart-gridline" }),
                                                _createElementVNode("div", { class: "chart-gridline" })
                                            ], -1)),
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.monthlyData, (bar, index) => {
                                                return (_openBlock(), _createElementBlock("div", {
                                                    key: bar.monthKey ?? bar.label,
                                                    class: "monthly-bar-col",
                                                    onClick: ($event) => (onMonthBarClick(bar))
                                                }, [
                                                    _createElementVNode("div", {
                                                        class: _normalizeClass(["monthly-bar", { 'monthly-bar-current': index === activeMonthIndex.value }]),
                                                        style: _normalizeStyle({ height: (animated.value ? monthlyBarHeight(bar.value) : 0) + 'px' })
                                                    }, null, 6)
                                                ], 8, _hoisted_39));
                                            }), 128))
                                        ], 4),
                                        (monthlyEmpty.value)
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_40, [
                                                _createElementVNode("img", {
                                                    class: "empty-icon",
                                                    src: require('@/assets/search.png'),
                                                    alt: ""
                                                }, null, 8, _hoisted_41),
                                                _cache[14] || (_cache[14] = _createElementVNode("span", { class: "empty-text" }, [
                                                    _createTextVNode("No transactions available"),
                                                    _createElementVNode("br"),
                                                    _createTextVNode("for these dates")
                                                ], -1))
                                            ]))
                                            : _createCommentVNode("", true),
                                        _createElementVNode("div", {
                                            class: "monthly-labels",
                                            style: _normalizeStyle({ minWidth: monthlyRowMinWidth.value + 'px' })
                                        }, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.monthlyData, (bar) => {
                                                return (_openBlock(), _createElementBlock("span", {
                                                    key: bar.monthKey ?? bar.label
                                                }, _toDisplayString(formatMonthlyLabel(bar.label)), 1));
                                            }), 128))
                                        ], 4)
                                    ], 2)
                                ])
                            ])),
                        _renderSlot(_ctx.$slots, "footer")
                    ], 64))
            ], 2));
        };
    }
});
